import React from "react"
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	TextField,
	FormControl,
	Select,
	MenuItem,
	Box,
} from "@mui/material"

import {useCustomerID} from "../useCustomerID"

import {useRevokeCustomerOffer} from "@indebted/api/customerOffersAPI"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"

type RevokeOfferModalProps = {
	offerID: string
	open: boolean
	onClose: () => void
	onSuccess: () => void
}

function RevokeOfferModal({offerID, open, onClose, onSuccess}: RevokeOfferModalProps) {
	const customerID = useCustomerID()
	const {notification} = useNotification()
	const [agentEmail] = useAgent()
	const [reason, setReason] = React.useState("INCORRECT_CONFIGURATION")
	const [notes, setNotes] = React.useState("")
	const revokeMutation = useRevokeCustomerOffer()

	const handleConfirm = async () => {
		try {
			await revokeMutation.mutateAsync({
				customerID,
				offerID,
				reason,
				agentEmail,
				notes: notes || undefined,
			})
			notification.success("Offer revoked successfully")
			onSuccess()
		} catch (error: any) {
			notification.error(error.Message || "Failed to revoke offer")
		}
	}

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
			<DialogContent sx={{pt: "24px", pb: 0, px: "24px"}}>
				<Box sx={{display: "flex", flexDirection: "column", gap: "12px", mb: "24px"}}>
					<DialogTitle
						sx={{
							p: 0,
							textAlign: "center",
							fontSize: "1.25rem",
							fontWeight: 500,
							lineHeight: 1.6,
							letterSpacing: "0.15px",
						}}
					>
						Remove Offer
					</DialogTitle>
					<DialogContentText
						sx={{
							textAlign: "center",
							fontSize: "0.875rem",
							lineHeight: 1.43,
							letterSpacing: "0.17px",
							color: "text.primary",
						}}
					>
						The customer will immediately lose access to the settlement discount offer.{" "}
						<Box component="span" sx={{color: "#D92D20"}}>
							This action cannot be undone.
						</Box>
					</DialogContentText>
				</Box>
				<Box sx={{display: "flex", flexDirection: "column", gap: "24px"}}>
					<FormControl fullWidth variant="outlined">
						<Select
							value={reason}
							onChange={(e) => setReason(e.target.value as string)}
							label="Reason"
							disabled={revokeMutation.isPending}
							displayEmpty
						>
							<MenuItem value="INCORRECT_CONFIGURATION">Incorrect configuration</MenuItem>
						</Select>
					</FormControl>
					<TextField
						fullWidth
						multiline
						rows={3}
						placeholder="Notes (optional)"
						variant="outlined"
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						disabled={revokeMutation.isPending}
					/>
				</Box>
			</DialogContent>
			<DialogActions sx={{flexDirection: "column", gap: "12px", px: "32px", pt: "24px", pb: "24px"}}>
				<Button
					fullWidth
					variant="contained"
					onClick={handleConfirm}
					disabled={revokeMutation.isPending}
					sx={{
						backgroundColor: "#D92D20",
						"&:hover": {backgroundColor: "#b91c1c"},
						borderRadius: "6px",
						textTransform: "uppercase",
						fontWeight: 500,
						letterSpacing: "0.4px",
					}}
				>
					{revokeMutation.isPending ? "Removing..." : "Remove Offer"}
				</Button>
				<Button
					fullWidth
					variant="contained"
					onClick={onClose}
					disabled={revokeMutation.isPending}
					sx={{
						backgroundColor: "#E9EAEB",
						color: "#717680",
						"&:hover": {backgroundColor: "#d1d5db"},
						boxShadow: "none",
						borderRadius: "6px",
						textTransform: "uppercase",
						fontWeight: 500,
						letterSpacing: "0.4px",
					}}
				>
					Go back
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export {RevokeOfferModal}
